import { t, useStoreLanguage, getLocale } from "../../../features/store-language/i18n.ts";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DirectionalIcon from "../../../features/navigation/DirectionalIcon.tsx";
import { Button, Empty, Skeleton } from "antd";
import ShopBreadcrumb from "../ShopBreadcrumb.tsx";
import AuthenticatedImage from "../../shared/AuthenticatedImage.tsx";
import "./ShopCategories.css";
import { getStoreCategories } from "../storefrontService.ts";
import { buildCategoryPath } from "../categoryUrl.ts";

interface ShopCategory {
  id: string | number;
  name?: string;
  title?: string;
  slug?: string;
  description?: string;
  fileId?: string | number;
  FileId?: string | number;
  childCategoryCount?: number;
}

function getCategoryName(category: ShopCategory) {
  return category.name || category.title || t("دسته‌بندی بدون عنوان");
}

function getCategoryPath(category: ShopCategory) {
  return buildCategoryPath(category.slug || category.id);
}

function CategoryCard({ category }: { category: ShopCategory }) {
  useStoreLanguage();
  const name = getCategoryName(category);
  const fileId = category.fileId ?? category.FileId;

  return (
    <Link className="shop-categories-card" to={getCategoryPath(category)}>
      <div className="shop-categories-card__media">
        {fileId ? (
          <AuthenticatedImage
            fileId={String(fileId)}
            disablePreview={true}
            originalImage={false}
            alt={t("تصویر دسته‌بندی {{value0}}", { value0: name })}
            className="shop-categories-card__image"
          />
        ) : (
          <img
            src="/assets/images/shop-modern-bazaar-fallback.png"
            alt={t("تصویر پیش‌فرض دسته‌بندی {{value0}}", { value0: name })}
            className="shop-categories-card__image"
          />
        )}
      </div>
      <div className="shop-categories-card__body">
        <h2>{name}</h2>
        {category.description ? <p>{category.description}</p> : null}
        {category.childCategoryCount > 0 ? (
          <span className="shop-categories-card__meta">
            {category.childCategoryCount.toLocaleString(getLocale())} {t("زیر‌دسته")} </span>
        ) : null}
      </div>
      <span className="shop-categories-card__action">
        <span>{t("مشاهده محصولات")}</span>
        <span className="shop-categories-card__action-icon">
          <DirectionalIcon
            direction="forward"
            size={17}
            weight="bold"
          />
        </span>
      </span>
    </Link>
  );
}

function CategoryCardSkeleton() {
  useStoreLanguage();
  return (
    <div className="shop-categories-card shop-categories-card--loading">
      <Skeleton.Image active />
      <div className="shop-categories-card__body">
        <Skeleton active title={{ width: "45%" }} paragraph={{ rows: 2 }} />
      </div>
    </div>
  );
}

function ShopCategories() {
  const storeLanguage = useStoreLanguage();
  const [categories, setCategories] = useState<ShopCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadCategories = useCallback(
    async function () {
      void storeLanguage; // Locale is part of this read/translation cache key.
      setLoading(true);
      setError(false);
      try {
        const response = await getStoreCategories();
        setCategories(Array.isArray(response) ? response : []);
      } catch (loadError) {
        console.error("Error loading active shop categories:", loadError);
        setCategories([]);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [storeLanguage],
  );

  useEffect(
    function () {
      void loadCategories();
    },
    [storeLanguage, loadCategories],
  );

  return (
    <>
      <ShopBreadcrumb
        className="shop-breadcrumb--products"
        hierarchy
        items={[{ title: t("دسته‌بندی‌ها") }]}
      />
      <div className="shop-categories-page">
        {loading ? (
          <div
            className="shop-categories-grid"
            aria-label={t("در حال بارگذاری دسته‌بندی‌ها")}
          >
            {[0, 1, 2, 3, 4, 5].map(function (item) {
              return <CategoryCardSkeleton key={item} />;
            })}
          </div>
        ) : error ? (
          <div className="shop-categories-state">
            <Empty description={t("بارگذاری دسته‌بندی‌ها انجام نشد")} />
            <Button type="primary" loading={loading} onClick={loadCategories}>{t("تلاش دوباره")} </Button>
          </div>
        ) : categories.length ? (
          <div className="shop-categories-grid">
            {categories.map(function (category) {
              return <CategoryCard category={category} key={category.id} />;
            })}
          </div>
        ) : (
          <div className="shop-categories-state">
            <Empty description={t("دسته‌بندی فعالی برای نمایش وجود ندارد")} />
          </div>
        )}
      </div>
    </>
  );
}

export default ShopCategories;
